import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const SeasonalTimeline = ({ seasonalData }) => {
  const [selectedMonth, setSelectedMonth] = useState(new Date()?.getMonth());

  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const currentSeasonData = seasonalData?.[selectedMonth];

  return (
    <div className="bg-[var(--color-card)] rounded-lg p-6 shadow-conservation border border-[var(--color-border)]">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-xl font-semibold text-[var(--color-foreground)]">Seasonal Wildlife Calendar</h3>
          <p className="text-sm text-[var(--color-muted-foreground)] mt-1">
            Discover the best times to observe different species
          </p>
        </div>
        <Icon name="Calendar" size={24} color="var(--color-primary)" />
      </div>
      {/* Month Selector */}
      <div className="mb-6 overflow-x-auto">
        <div className="flex gap-2 pb-2">
          {months?.map((month, index) => (
            <button
              key={index}
              onClick={() => setSelectedMonth(index)}
              className={`flex-shrink-0 px-4 py-2 rounded-lg text-sm font-medium animation-organic ${
                selectedMonth === index
                  ? 'bg-[var(--color-primary)] text-[var(--color-primary-foreground)]'
                  : 'bg-[var(--color-muted)] text-[var(--color-muted-foreground)] hover:bg-[var(--color-muted)]/80'
              }`}
            >
              {month}
            </button>
          ))}
        </div>
      </div>
      {/* Season Info */}
      <div className="grid md:grid-cols-3 gap-4 mb-6">
        <div className="bg-[var(--color-muted)]/50 p-4 rounded-lg border border-[var(--color-border)]">
          <div className="flex items-center gap-2 mb-2">
            <Icon name="Cloud" size={20} color="var(--color-info)" />
            <span className="text-sm font-semibold text-[var(--color-foreground)]">Weather</span>
          </div>
          <p className="text-sm text-[var(--color-muted-foreground)]">{currentSeasonData?.weather}</p>
        </div>

        <div className="bg-[var(--color-muted)]/50 p-4 rounded-lg border border-[var(--color-border)]">
          <div className="flex items-center gap-2 mb-2">
            <Icon name="Thermometer" size={20} color="var(--color-warning)" />
            <span className="text-sm font-semibold text-[var(--color-foreground)]">Temperature</span>
          </div>
          <p className="text-sm text-[var(--color-muted-foreground)]">{currentSeasonData?.temperature}</p>
        </div>

        <div className="bg-[var(--color-muted)]/50 p-4 rounded-lg border border-[var(--color-border)]">
          <div className="flex items-center gap-2 mb-2">
            <Icon name="Users" size={20} color="var(--color-secondary)" />
            <span className="text-sm font-semibold text-[var(--color-foreground)]">Visitor Level</span>
          </div>
          <p className="text-sm text-[var(--color-muted-foreground)]">{currentSeasonData?.visitorLevel}</p>
        </div>
      </div>
      {/* Wildlife Activity */}
      <div className="space-y-4">
        <h4 className="text-sm font-semibold text-[var(--color-foreground)]">Active Wildlife This Month</h4>
        
        <div className="space-y-3">
          {currentSeasonData?.activeSpecies?.map((species, index) => (
            <div key={index} className="flex items-start gap-3 p-3 bg-[var(--color-muted)]/50 rounded-lg border border-[var(--color-border)]">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                species?.activityLevel === 'High' ? 'bg-[var(--color-success)]/20' :
                species?.activityLevel === 'Medium'? 'bg-[var(--color-warning)]/20' : 'bg-[var(--color-info)]/20'
              }`}>
                <Icon 
                  name={species?.icon} 
                  size={20} 
                  color={
                    species?.activityLevel === 'High' ? 'var(--color-success)' :
                    species?.activityLevel === 'Medium' ? 'var(--color-warning)' :
                    'var(--color-info)'
                  }
                />
              </div>
              <div className="flex-1">
                <div className="flex items-center justify-between mb-1">
                  <h5 className="text-sm font-semibold text-[var(--color-foreground)]">{species?.name}</h5>
                  <span className={`text-xs px-2 py-0.5 rounded ${
                    species?.activityLevel === 'High' ? 'bg-[var(--color-success)]/20 text-[var(--color-success)]' :
                    species?.activityLevel === 'Medium'? 'bg-[var(--color-warning)]/20 text-[var(--color-warning)]' : 'bg-[var(--color-info)]/20 text-[var(--color-info)]'
                  }`}>
                    {species?.activityLevel} Activity
                  </span>
                </div>
                <p className="text-xs text-[var(--color-muted-foreground)] mb-2">{species?.behavior}</p>
                <div className="flex items-center gap-2 text-xs text-[var(--color-muted-foreground)]">
                  <Icon name="MapPin" size={12} />
                  <span>Best spotted in: {species?.location}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      {/* Special Events */}
      {currentSeasonData?.specialEvents && currentSeasonData?.specialEvents?.length > 0 && (
        <div className="mt-6 p-4 bg-[var(--color-accent)]/10 border border-[var(--color-accent)]/20 rounded-lg">
          <div className="flex items-start gap-3">
            <Icon name="Star" size={20} color="var(--color-accent)" className="flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="text-sm font-semibold text-[var(--color-foreground)] mb-2">Special Events This Month</h4>
              <ul className="space-y-1">
                {currentSeasonData?.specialEvents?.map((event, index) => (
                  <li key={index} className="text-sm text-[var(--color-muted-foreground)]">• {event}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}
      {/* Action Button */}
      <div className="mt-6">
        <Button variant="default" iconName="Calendar" iconPosition="left" fullWidth>
          Plan Visit for {months?.[selectedMonth]}
        </Button>
      </div>
    </div>
  );
};

export default SeasonalTimeline;